import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Trash2, ShoppingBag, Edit2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import axiosInstance from "@/services/axios";
import { API } from "@/config/api";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = "http://127.0.0.1:8000";
const API_URL = `${BACKEND_URL}/api/v1`;

export const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    loadCart();
    loadShippingMethods();
  }, []);

  // Listen for storage events (updates from other tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      loadCart();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    setCartItems(cart);
  };

  const loadShippingMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/shipping/methods`);
      setShippingMethods(response.data);
      if (response.data.length > 0) {
        setSelectedShipping(response.data[2].id);
      }
    } catch (error) {
      console.error("Error loading shipping methods:", error);
    }
  };

  const syncCartWithServer = async (updatedCart) => {
    if (!isAuthenticated) return;

    setIsSyncing(true);
    try {
      console.log("Syncing cart to server:", updatedCart);
      const response = await axiosInstance.post(API.auth.cart.sync, {
        items: updatedCart,
      });
      console.log("Server sync response:", response.data);

      // Verify the server has the updated cart
      const verifyResponse = await axiosInstance.get(API.auth.cart.get);
      console.log("Server cart after sync:", verifyResponse.data);

      return true;
    } catch (error) {
      console.error(
        "Failed to sync cart:",
        error.response?.data || error.message,
      );
      toast.error("Failed to sync cart with server");
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const removeItem = async (itemId) => {
    console.log("Removing item:", itemId);
    console.log("Current cart:", cartItems);

    const updatedCart = cartItems.filter((item) => item.id !== itemId);
    console.log("Updated cart:", updatedCart);

    // Update state and localStorage
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));

    // Sync with server if logged in
    if (isAuthenticated) {
      const success = await syncCartWithServer(updatedCart);
      if (success) {
        toast.success("Item removed from cart");
      }
    } else {
      toast.success("Item removed from cart");
    }

    // Trigger storage event for other components
    window.dispatchEvent(new Event("storage"));
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1 || newQuantity > 10) return;

    console.log("Updating quantity:", { itemId, newQuantity });

    const updatedCart = cartItems.map((item) => {
      if (item.id === itemId) {
        const unitPrice = item.line_total / item.quantity;
        return {
          ...item,
          quantity: newQuantity,
          line_total: unitPrice * newQuantity,
        };
      }
      return item;
    });

    console.log("Updated cart:", updatedCart);

    // Update state and localStorage
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));

    // Sync with server if logged in
    if (isAuthenticated) {
      await syncCartWithServer(updatedCart);
    }

    // Trigger storage event for other components
    window.dispatchEvent(new Event("storage"));
  };

  const handleEditItem = (item) => {
    sessionStorage.setItem(
      "editingCartItem",
      JSON.stringify({
        id: item.id,
        file_id: item.file_id,
        metal_id: item.metal_id,
        services: item.services,
        quantity: item.quantity,
      }),
    );
    navigate(`/viewer/${item.file_id}?edit=true`);
  };

  const proceedToCheckout = () => {
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (!selectedShipping) {
      toast.error("Please select a shipping method");
      return;
    }

    if (isAuthenticated) {
      setIsRedirecting(true);
      setTimeout(() => {
        navigate("/checkout");
      }, 100);
    } else {
      setShowAuthModal(true);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.line_total, 0);
  const shippingCost =
    shippingMethods.find((m) => m.id === selectedShipping)?.price || 0;
  const total = subtotal + shippingCost;

  if (cartItems.length === 0) {
    return (
      <div
        className="min-h-screen bg-background py-24 px-6"
        data-testid="empty-cart"
      >
        <div className="max-w-4xl mx-auto text-center">
          <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-muted-foreground" />
          <h2 className="font-heading text-3xl font-bold mb-4">
            Your Cart is Empty
          </h2>
          <p className="text-muted-foreground mb-8">
            Start by uploading a 3D model to get a quote
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-accent hover:bg-accent-hover text-white px-8 py-6"
            data-testid="go-home-button"
          >
            Upload Model
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h1
          className="font-heading text-4xl font-bold mb-8"
          data-testid="cart-title"
        >
          Shopping Cart
        </h1>

        {/* Auth Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-md w-full rounded-md p-6 relative">
              <h3 className="font-heading text-xl font-bold mb-4">
                Complete Your Order
              </h3>

              <div className="space-y-4">
                <div className="border border-border rounded-md p-4">
                  <h4 className="font-semibold mb-2">Have an account?</h4>
                  <Button
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate("/login", { state: { from: "/checkout" } });
                    }}
                    className="w-full bg-accent hover:bg-accent-hover text-white mb-2"
                  >
                    Sign In
                  </Button>
                </div>

                <div className="border border-border rounded-md p-4">
                  <h4 className="font-semibold mb-2">New customer?</h4>
                  <Button
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate("/checkout");
                    }}
                    variant="outline"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-white mb-3"
                  >
                    Continue as Guest
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate("/register", { state: { from: "/checkout" } });
                    }}
                    variant="ghost"
                    className="w-full text-sm"
                  >
                    Create an account for faster checkout
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground bg-secondary p-3 rounded">
                  <p className="font-medium mb-1">Benefits of an account:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Track your orders</li>
                    <li>View order history</li>
                    <li>Save addresses for next time</li>
                    <li>Download invoices</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setShowAuthModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cartItems.map((item, idx) => (
                <div
                  key={item.id}
                  className="bg-white border border-border p-6 rounded-md"
                  data-testid={`cart-item-${idx}`}
                >
                  <div className="flex gap-4">
                    {item.thumbnail && (
                      <div className="w-24 h-24 flex-shrink-0">
                        <img
                          src={item.thumbnail}
                          alt={item.filename}
                          className="w-full h-full object-cover rounded border border-border"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">
                        {item.filename}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        Metal:{" "}
                        <span className="font-medium text-foreground">
                          {item.metal_name}
                        </span>
                      </p>
                      {item.services.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          + {item.services.length} additional service(s)
                        </p>
                      )}

                      <div className="flex items-center gap-4 mt-4">
                        <label className="text-sm font-medium">Quantity:</label>
                        <div className="flex items-center border border-input rounded-sm overflow-hidden w-32">
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.id, item.quantity - 1)
                            }
                            disabled={item.quantity <= 1 || isSyncing}
                            className={`px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors ${
                              item.quantity <= 1 || isSyncing
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            −
                          </button>

                          <input
                            type="text"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*$/.test(value)) {
                                const newValue =
                                  value === "" ? 1 : parseInt(value);
                                if (newValue >= 1 && newValue <= 10) {
                                  updateQuantity(item.id, newValue);
                                }
                              }
                            }}
                            className="w-full text-center outline-none py-2"
                            min="1"
                            max="10"
                            disabled={isSyncing}
                          />

                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.id, item.quantity + 1)
                            }
                            disabled={item.quantity >= 10 || isSyncing}
                            className={`px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors ${
                              item.quantity >= 10 || isSyncing
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            +
                          </button>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Max: 10
                        </span>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(item)}
                          disabled={isSyncing}
                          className="text-accent hover:text-accent-hover"
                          data-testid={`edit-item-${idx}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Change Metal
                        </Button>
                      </div>
                    </div>

                    <div className="text-right">
                      <div
                        className="font-heading font-bold text-xl text-accent mb-4"
                        data-testid={`item-price-${idx}`}
                      >
                        ${item.line_total.toFixed(2)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        disabled={isSyncing}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-white border border-border p-6 rounded-md">
              <h3 className="font-semibold mb-4">Shipping Method</h3>
              <div className="space-y-2">
                {shippingMethods.map((method) => (
                  <label
                    key={method.id}
                    className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer hover:border-accent transition-colors"
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value={method.id}
                      checked={selectedShipping === method.id}
                      onChange={(e) => setSelectedShipping(e.target.value)}
                      className="text-accent"
                      data-testid={`shipping-${method.id}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{method.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {method.estimated_days}
                      </div>
                    </div>
                    <div className="font-mono text-sm">
                      ${method.price.toFixed(2)}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white border border-border p-6 rounded-md sticky top-24">
              <h3 className="font-heading font-semibold text-xl mb-6">
                Order Summary
              </h3>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span>
                    Subtotal (
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)}{" "}
                    items):
                  </span>
                  <span className="font-mono" data-testid="cart-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping:</span>
                  <span className="font-mono" data-testid="cart-shipping">
                    ${shippingCost.toFixed(2)}
                  </span>
                </div>
                <div className="pt-3 border-t border-border flex justify-between">
                  <span className="font-semibold">Total:</span>
                  <span
                    className="font-heading font-bold text-2xl text-accent"
                    data-testid="cart-total"
                  >
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="w-full border border-input rounded-sm px-4 py-3 text-sm mb-2"
                  data-testid="coupon-input"
                />
                <Button
                  variant="outline"
                  className="w-full"
                  size="sm"
                  data-testid="apply-coupon-button"
                  disabled={isSyncing}
                >
                  Apply Coupon
                </Button>
              </div>

              <Button
                onClick={proceedToCheckout}
                disabled={isRedirecting || isSyncing}
                className="w-full bg-accent hover:bg-accent-hover text-white px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="proceed-checkout-button"
              >
                {isRedirecting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    REDIRECTING...
                  </>
                ) : isSyncing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    SYNCING...
                  </>
                ) : (
                  "Proceed to Checkout"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
